import { POSTHOG_SCHEMA_VERSION } from '@/lib/posthogIdentity';
import { trackEvent } from '@/lib/trackEvent';

const SESSION_KEY_PREFIX = 'ph_report_performance:';
const ALLOWED_CONNECTION_TYPES = new Set(['slow-2g', '2g', '3g', '4g']);
const MEDIA_WAIT_TIMEOUT_MS = 15_000;

type NavigatorConnection = {
  effectiveType?: string;
};

type ReportPerfMarks = {
  startedAt: number;
  firstContentAt?: number;
  interactiveAt?: number;
};

const marksByReport = new Map<string, ReportPerfMarks>();

const getSessionKey = (reportId: string) => `${SESSION_KEY_PREFIX}${reportId}`;

const hasTracked = (reportId: string): boolean => {
  if (typeof sessionStorage === 'undefined') return false;
  return sessionStorage.getItem(getSessionKey(reportId)) === '1';
};

const markTracked = (reportId: string) => {
  if (typeof sessionStorage === 'undefined') return;
  sessionStorage.setItem(getSessionKey(reportId), '1');
};

const getConnectionType = (): string => {
  const connection = (navigator as Navigator & { connection?: NavigatorConnection })
    .connection;
  const effectiveType = connection?.effectiveType;
  if (effectiveType && ALLOWED_CONNECTION_TYPES.has(effectiveType)) {
    return effectiveType;
  }
  return 'unknown';
};

const roundMs = (value: number): number => Math.max(0, Math.round(value));

const ensureMarks = (reportId: string): ReportPerfMarks => {
  const existing = marksByReport.get(reportId);
  if (existing) return existing;
  const created: ReportPerfMarks = { startedAt: performance.now() };
  marksByReport.set(reportId, created);
  return created;
};

export const markReportPerformanceStart = (reportId: string): void => {
  if (!reportId || typeof performance === 'undefined') return;
  marksByReport.set(reportId, { startedAt: performance.now() });
};

export const markReportFirstContent = (reportId: string): void => {
  if (!reportId || typeof performance === 'undefined') return;
  const marks = ensureMarks(reportId);
  if (marks.firstContentAt !== undefined) return;
  marks.firstContentAt = performance.now();
};

const waitForMedia = (root: ParentNode | null): Promise<void> => {
  if (!root) return Promise.resolve();

  const media = Array.from(root.querySelectorAll('img, video'));
  if (media.length === 0) return Promise.resolve();

  const pending = media.map((element) => {
    if (element instanceof HTMLImageElement) {
      if (element.complete) return Promise.resolve();
      return new Promise<void>((resolve) => {
        element.addEventListener('load', () => resolve(), { once: true });
        element.addEventListener('error', () => resolve(), { once: true });
      });
    }

    if (element instanceof HTMLVideoElement) {
      if (element.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
        return Promise.resolve();
      }
      return new Promise<void>((resolve) => {
        element.addEventListener('loadeddata', () => resolve(), { once: true });
        element.addEventListener('error', () => resolve(), { once: true });
      });
    }

    return Promise.resolve();
  });

  return Promise.race([
    Promise.all(pending).then(() => undefined),
    new Promise<void>((resolve) => {
      window.setTimeout(resolve, MEDIA_WAIT_TIMEOUT_MS);
    }),
  ]);
};

const emitMetrics = (
  reportId: string,
  marks: ReportPerfMarks,
  totalAt: number
): boolean => {
  if (hasTracked(reportId)) return true;

  const startedAt = marks.startedAt;
  const firstContentAt = marks.firstContentAt ?? marks.interactiveAt ?? totalAt;
  const interactiveAt = marks.interactiveAt ?? firstContentAt;

  const timeToFirstContent = roundMs(firstContentAt - startedAt);
  const timeToInteractive = roundMs(Math.max(firstContentAt, interactiveAt) - startedAt);
  const totalLoadTime = roundMs(Math.max(interactiveAt, totalAt) - startedAt);

  if (
    timeToInteractive < timeToFirstContent ||
    totalLoadTime < timeToInteractive
  ) {
    return false;
  }

  const tracked = trackEvent('report_performance', {
    time_to_first_content_ms: timeToFirstContent,
    time_to_interactive_ms: timeToInteractive,
    total_load_time_ms: totalLoadTime,
    connection_type: getConnectionType(),
    report_id: reportId,
    schema_version: POSTHOG_SCHEMA_VERSION,
  });

  if (tracked) {
    markTracked(reportId);
    marksByReport.delete(reportId);
  }

  return tracked;
};

const afterNextPaint = (): Promise<number> =>
  new Promise((resolve) => {
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        resolve(performance.now());
      });
    });
  });

export const startReportPerformanceTracking = (
  reportId: string,
  root?: ParentNode | null
): (() => void) => {
  if (!reportId || typeof window === 'undefined' || typeof performance === 'undefined') {
    return () => undefined;
  }

  if (hasTracked(reportId)) {
    return () => undefined;
  }

  const marks = ensureMarks(reportId);
  let stopped = false;

  const finish = () => {
    if (stopped || hasTracked(reportId)) return;
    if (marks.interactiveAt === undefined) return;
    emitMetrics(reportId, marks, performance.now());
  };

  afterNextPaint().then(async (interactiveAt) => {
    if (stopped || hasTracked(reportId)) return;
    marks.interactiveAt = Math.max(interactiveAt, marks.firstContentAt ?? interactiveAt);
    await waitForMedia(root ?? null);
    if (stopped) return;
    finish();
  });

  return () => {
    stopped = true;
  };
};
